<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { internationalPhoneForWidget, storedInternationalPhone, type PhoneCountry } from '../formFields';

interface InternationalPhoneInstance {
	destroy: () => void;
	getSelectedCountryData: () => PhoneCountry;
	setNumber: (value: string) => void;
}

declare global {
	interface Window {
		intlTelInput?: (input: HTMLInputElement, options: Record<string, unknown>) => InternationalPhoneInstance;
	}
}

const props = defineProps<{
	id: string;
	modelValue: string;
	required?: boolean;
}>();

const emit = defineEmits<{
	'update:modelValue': [value: string];
}>();

const input = ref<HTMLInputElement | null>(null);
let phone: InternationalPhoneInstance | null = null;

const currentValue = (): string => {
	if (!input.value) return '';
	if (!phone) return input.value.value.trim();
	return storedInternationalPhone( phone.getSelectedCountryData().dialCode, input.value.value );
};

const update = (): void => emit('update:modelValue', currentValue());

const sync = (value: string): void => {
	if (!input.value || currentValue() === value) return;
	if (phone) phone.setNumber( internationalPhoneForWidget( value ) );
	else input.value.value = value;
};

onMounted(() => {
	if (!input.value) return;
	if (window.intlTelInput) {
		phone = window.intlTelInput(input.value, {
			initialCountry: 'us',
			nationalMode: false,
			separateDialCode: true,
		});
		input.value.addEventListener('countrychange', update);
	}
	sync(props.modelValue);
});

watch(() => props.modelValue, sync);

onBeforeUnmount(() => {
	input.value?.removeEventListener('countrychange', update);
	phone?.destroy();
});
</script>

<template>
	<div class="international-phone">
		<input
			:id="id"
			ref="input"
			type="tel"
			:required="required"
			@input="update"
		>
	</div>
</template>
